/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Režňák (reznak@cadwork.cz)
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers
import {vec4, vec3, vec2} from "gl-matrix";
import {Tools} from "App/environment/tools";
import MovableObject from "App/environment/movable_object";
import WebGLContext from "App/web_gl_context";
import Pass from "App/render/webgl/passes/pass";
import {getDOMInlinedImage} from "App/storage/utils";
import IScene from "js_interface/src/scene_interface";

/**
 * Abstract object for rendering
 *
 * @param {SceneAPI.Scene} sceneAPI
 * @param {WebGLRenderingContext} gl
 * @param {Array} vertices
 * @param {Array} indices
 * @param {int} positions
 * @param {int} normals
 * @param {int} texcoords
 * @param {boolean} [dynamic=false]
 *
 * @extends MovableObject
 * @constructor
 */
export default
class DrawableObject extends MovableObject {

    protected _vboMethod: any;
    protected _eboMethod: any;

    private _sceneAPI: IScene;
    private _gl: WebGLRenderingContext;

    private _positionCount: number;
    private _normalCount: number;
    private _texcoordCount: number;

    private _culling: boolean;
    private _cullFace: any;
    private _frontFace: any;

    private _textureScale: vec2;
    private _color: vec4;
    private _placeholderColor: vec4;
    private _activeTextures: any[];
    private _images: HTMLImageElement[];
    private _textures: WebGLTexture[];
    private _dynamic: boolean;
    private _vbo: any;
    private _ebo = null;
    private _vboCount: number;
    private _vboOffset: number;
    private _eboCount: number;
    private _eboOffset: number;

    // Some variables
    private _visible: boolean;

    constructor(sceneAPI: IScene, gl: WebGLRenderingContext, attributes: number[], indices: number[], positions: number, normals: number, texcoords: number, dynamic: boolean) {
        super();

        this._sceneAPI = sceneAPI;
        this._gl = gl;

        // Buffers
        this._positionCount = positions;
        this._normalCount = normals;
        this._texcoordCount = texcoords;

        // OpenGL
        this._culling = true;
        this._cullFace = gl.BACK;
        this._frontFace = gl.CCW;

        // Textures
        this._textureScale = vec2.fromValues(1.0, 1.0);
        this._color = vec4.fromValues(1.0, 1.0, 1.0, 1.0);    // If without texture, use color
        this._placeholderColor = vec4.fromValues(255, 255, 255, 255);
        this._activeTextures = [];
        /** @type Image[] */
        this._images = [];
        /** @type WebGLTexture[] */
        this._textures = [];

        // Data
        this._dynamic = dynamic === undefined ? false : dynamic;
        this._vbo = null;
        this._ebo = null;
        this._vboCount = 0;
        this._vboOffset = 0;
        this._vboMethod = gl.TRIANGLES;
        this._eboCount = 0;
        this._eboOffset = 0;
        this._eboMethod = gl.TRIANGLES;

        // Some variables
        this._visible = true;

        // Init
        this._create(attributes, indices);
    }

    /**
     * Release buffers (not textures)
     */
    public release(): void {
        if (this._vbo !== null) {
            this._gl.deleteBuffer(this._vbo);
            this._vbo = null;
        }

        if (this._ebo !== null) {
            this._gl.deleteBuffer(this._ebo);
            this._ebo = null;
        }
    }

    /**
     * @param {number} r
     * @param {number} g
     * @param {number} b
     */
    public setPlaceholderColor(r: number, g: number, b: number): void {
        this._placeholderColor[0] = r;
        this._placeholderColor[1] = g;
        this._placeholderColor[2] = b;
    }

    /**
     * @param {number} s
     * @param {number} t
     */
    public setTextureScale(s: number, t: number): void {
        this._textureScale[0] = s;
        this._textureScale[1] = t;
    }

    /**
     * @return {vec4}
     */
    public getColor(): vec4 {
        return this._color;
    }

    /**
     * @param {number} index
     * @return {WebGLTexture | null}
     */
    public getTexture(index: number): WebGLTexture {
        let tx = this._textures[index];
        if (tx) {
            return tx;
        }
        return null;
    }

    /**
     * @param {number} index
     * @return {Image | null}
     */
    public getImage(index: number): HTMLImageElement {
        let img = this._images[index];
        if (img) {
            return img;
        }
        return null;
    }


    /**
     * @param {number} r
     * @param {number} g
     * @param {number} b
     */
    public addColor(r: number, g: number, b: number): void {
        this._color[0] += r;
        this._color[1] += g;
        this._color[2] += b;

        for (let i = 0; i < vec4.length(this._color); i++) {
            if (this._color[i] > 1.0) {
                this._color[i] = 1.0;
            }
        }
    }

    /**
     * @return {boolean}
     */
    public isVisible(): boolean {
        return this._visible;
    }

    /**
     * @param {boolean} visible
     */
    public setVisible(visible: boolean): void {
        this._visible = visible;
    }

    /**
     * @param {number} r
     * @param {number} g
     * @param {number} b
     * @param {number} [a = 1.0]
     */
    public setColor(r: number, g: number, b: number, a?: number): void {
        this._color[0] = r;
        this._color[1] = g;
        this._color[2] = b;
        this._color[3] = (a === undefined) ? 1.0 : a;
    }

    /**
     * @param {vec3} rgb
     */
    public setColor3(rgb: vec3): void {
        this._color[0] = rgb[0];
        this._color[1] = rgb[1];
        this._color[2] = rgb[2];
        this._color[3] = 1.0;
    }

    /**
     * @param {vec4} rgba
     */
    public setColor4(rgba: vec4): void {
        this._color[0] = rgba[0];
        this._color[1] = rgba[1];
        this._color[2] = rgba[2];
        this._color[3] = rgba[3];
    }

    /**
     * @param {number} a
     */
    public setAlpha(a: number): void {
        this._color[3] = a;
    }

    /**
     * @return {number} first unused index for texture
     */
    public getUnusedTextureIndex(): number {
        for (let i = 0; i <= this._activeTextures.length; i++) {
            if (!this._activeTextures[i]) {
                return i;
            }
        }
        return 0;
    }

    /**
     * Set texture to specific index
     *
     * @param {number} index
     * @param {string} name or blob url
     * @param {boolean} [repeat = false]
     * @param {boolean} [optimal = false]
     * @param {boolean} [mipmap = true]
     */

    public setTexture(index: number, url: string, repeat: boolean, optimal: boolean, mipmap: boolean): void
    {
        if (url === '') {
            return;
        }

        if (url.indexOf('blob:') === -1) { // do not modify name file path for url blobs
            let imageData: string = getDOMInlinedImage(url);
            if (imageData) {
                url = imageData;
            }
        }

        this.activateTexture(index);

        Tools.getInstance().getImageManager().getImage(url, (img: HTMLImageElement) => {
            this._images[index] = img;
            let rep = repeat !== undefined && repeat;
            let opt = optimal !== undefined && optimal;
            let mip = opt && (mipmap === undefined || mipmap);
            this._onLoadImage(index, rep, opt, mip);

            // Redraw with texture
            this._sceneAPI.viewModeModule.scheduleRedraw();
        });
    }

    /**
     * Create texture in specific index without data
     *
     * @param {number} index
     * @param {boolean} [repeat = false]
     * @param {boolean} [optimal = false]
     */
    public reserveTexture(index: number, repeat: boolean, optimal: boolean): void {
        this.activateTexture(index);
        this._createEmptyTexture(index, repeat, optimal);
    }

    /**
     * @param {number} index
     */
    public activateTexture(index: number): void {
        this._activeTextures[index] = true;
    }

    /**
     * @param {number} index
     */
    public deactivateTexture(index: number): void {
        this._activeTextures[index] = false;
    }

    /**
     * @param {Pass} renderPass
     * @param {number}  [instances = 1]                 Number of instances
     */
    public draw(renderPass: Pass, instances: number): void
    {
        /* jshint camelcase:false */
        if (!this.isVisible()) {
            return;
        }

        let gl: any = this._gl;
        let shader: any = renderPass.getActiveShaderRenderable();
        instances = (instances === undefined) ? 1 : instances;

        let vertexAttribDivisorMethodRef;
        let drawArraysInstancedMethdRef;
        let drawElementsInstancedMethdRef;
        if (WebGLContext.isWebGL2) {
            vertexAttribDivisorMethodRef = gl.vertexAttribDivisor.bind(gl);
            drawArraysInstancedMethdRef = gl.drawArraysInstanced.bind(gl);
            drawElementsInstancedMethdRef = gl.drawElementsInstanced.bind(gl);
        }
        else {
            let ctx: any = WebGLContext.getFeatureAsExtension("INSTANCING");
            vertexAttribDivisorMethodRef = ctx.vertexAttribDivisorANGLE.bind(ctx);
            drawArraysInstancedMethdRef = ctx.drawArraysInstancedANGLE.bind(ctx);
            drawElementsInstancedMethdRef = ctx.drawElementsInstancedANGLE.bind(ctx);
        }

        // Test for instanced drawing
        let drawInstanced = instances > 1;
        if (drawInstanced && !WebGLContext.useInstancing) {
            drawInstanced = false;
        }

        // Set OpenGL mode
        if (this._culling) {
            gl.enable(gl.CULL_FACE);
            gl.cullFace(this._cullFace);
            gl.frontFace(this._frontFace);
        }
        else {
            gl.disable(gl.CULL_FACE);
        }

        // Prepare uniforms
        gl.uniformMatrix4fv(shader.uniformLocations.u_modelMatrix, false, this.getModelMatrix() as unknown as Float32List);
        gl.uniform1f(shader.uniformLocations.u_alpha, this._color[3]);

        // Bind buffer and start
        gl.bindBuffer(gl.ARRAY_BUFFER, this._vbo);
        let step = 4 * (this._positionCount + this._normalCount + this._texcoordCount);

        // Set positions
        gl.enableVertexAttribArray(shader.attributeLocations.a_position);
        gl.vertexAttribPointer(shader.attributeLocations.a_position, this._positionCount, gl.FLOAT, false, step, 0);
        if (drawInstanced) {
            vertexAttribDivisorMethodRef(shader.attributeLocations.a_position, 0);
        }

        // Set normals
        if (shader.attributeLocations.a_normal !== undefined) {
            gl.enableVertexAttribArray(shader.attributeLocations.a_normal);
            gl.vertexAttribPointer(shader.attributeLocations.a_normal, this._normalCount, gl.FLOAT, false, step, 4 * this._positionCount);
            if (drawInstanced) {
                vertexAttribDivisorMethodRef(shader.attributeLocations.a_normal, 0);
            }
        }

        // Set texcoords and texture
        if (shader.attributeLocations.a_texcoords !== undefined) {
            // Set attributes
            gl.enableVertexAttribArray(shader.attributeLocations.a_texcoords);
            gl.vertexAttribPointer(shader.attributeLocations.a_texcoords, this._texcoordCount, gl.FLOAT, false, step,
                4 * (this._positionCount + this._normalCount));
            if (drawInstanced) {
                vertexAttribDivisorMethodRef(shader.attributeLocations.a_texcoords, 0);
            }

            // Common uniforms for textures
            gl.uniform2fv(shader.uniformLocations.u_textureScale, this._textureScale as unknown as Float32List);

            // Set textures
            for (let i = 0; i < this._activeTextures.length; i++) {
                // Test if active
                if (!this._activeTextures[i]) {
                    continue;
                }

                // Test location in shader
                if (!shader.uniformLocations["u_texture" + i]) {
                    continue;
                }

                // Activate
                gl.activeTexture(gl["TEXTURE" + i]);
                gl.uniform1i(shader.uniformLocations["u_texture" + i], i);
                if (this._textures[i]) {
                    gl.bindTexture(gl.TEXTURE_2D, this._textures[i]);
                }
                else {
                    gl.bindTexture(gl.TEXTURE_2D, Tools.getInstance().getPlaceholderTexture(gl, this._placeholderColor));
                }
            }
        }
        // Set only a single color
        else {
            gl.uniform4fv(shader.uniformLocations.u_color, this._color as unknown as Float32List);
        }

        // Instancing
        if (drawInstanced) {
            // Draw it via EBO or VBO
            if (this._ebo) {
                gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this._ebo);
                drawElementsInstancedMethdRef(this._eboMethod, this._eboCount, gl.UNSIGNED_INT, this._eboOffset, instances);
            }
            else {
                drawArraysInstancedMethdRef(this._vboMethod, this._vboOffset, this._vboCount, instances);
            }
        }
        else {
            // Draw via EBO or VBO
            if (this._ebo) {
                gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this._ebo);
                gl.drawElements(this._eboMethod, this._eboCount, gl.UNSIGNED_INT, this._eboOffset);
            }
            else {
                gl.drawArrays(this._vboMethod, this._vboOffset, this._vboCount);
            }
        }

        // CleanUp
        if (shader.attributeLocations.a_position !== undefined) {
            gl.disableVertexAttribArray(shader.attributeLocations.a_position);
        }
        if (shader.attributeLocations.a_normal !== undefined) {
            gl.disableVertexAttribArray(shader.attributeLocations.a_normal);
        }
        if (shader.attributeLocations.a_texcoords !== undefined) {
            gl.disableVertexAttribArray(shader.attributeLocations.a_texcoords);
        }

        gl.bindBuffer(gl.ARRAY_BUFFER, null);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
    }

    /**
     * Update data in existing vertex buffer object
     *
     * @param {Array} attributes
     * @protected
     */
    protected _updateVBO(attributes): void {
        let gl = this._gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this._vbo);
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, new Float32Array(attributes));
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
    }

    /**
     * @param {Array} vertices
     * @param {Array} indices
     * @private
     */
    private _create(attributes, indices): void {
        this._vbo = Tools.getInstance().createVBO(this._gl, attributes, this._dynamic);
        this._vboCount = attributes.length / (this._positionCount + this._normalCount + this._texcoordCount);

        if (indices !== undefined && indices !== null) {
            this._eboCount = indices.length;
            this._ebo = Tools.getInstance().createEBO(this._gl, indices);
        }
    }

    /**
     * @param {number} index
     * @param {boolean} repeat
     * @param {boolean} optimal
     * @param {boolean} mipmap
     * @private
     */
    private _onLoadImage(index: number, repeat: boolean, optimal: boolean, mipmap: boolean): void {
        let gl = this._gl;

        this._createEmptyTexture(index, repeat, optimal);

        // Load data
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this._images[index]);

        // Test for mipmap
        if (optimal && mipmap) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
            gl.generateMipmap(gl.TEXTURE_2D);
        }

        gl.bindTexture(gl.TEXTURE_2D, null);

        // Schedule redraw when texture is ready
        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    /**
     * @param {number} index
     * @param {boolean} repeat
     * @param {boolean} optimal
     * @private
     */
    private _createEmptyTexture(index: number, repeat: boolean, optimal: boolean): void {
        let gl = this._gl;

        // Delete old one
        if (this._textures[index] !== null) {
            gl.deleteTexture(this._textures[index]);
            this._textures[index] = null;
        }

        // Create new
        this._textures[index] = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, this._textures[index]);

        if (!repeat) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        }
        else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);

        }

        if (!optimal) {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        }
        else {
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

            if (WebGLContext.getFeatureAvailable("TEXTURE_FILTER_ANISOTROPIC")) {
                let ext = WebGLContext.getFeatureAsExtension("TEXTURE_FILTER_ANISOTROPIC");
                if (ext) {
                    // @ts-ignore
                    gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, WebGLContext.caps.MAX_TEXTURE_MAX_ANISOTROPY_EXT_VALUE);
                }
            }
        }
    }
}
